import React, { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { Box } from '../ui';
import AppErrorBoundary from '../components/AppErrorBoundary';
import { CssBaseline } from '../ui';
import { UserPreferencesProvider } from '../contexts/UserPreferencesContext';
import { LookProvider } from '../contexts/LookContext';
import { NotificationsProvider } from '../contexts/NotificationsContext';
import { LocalizationProvider } from '../contexts/LocalizationProvider';
import { PickerLocaleProvider } from '../contexts/PickerLocaleProvider';
import { ToastProvider } from '../contexts/ToastContext';
import { ROUTES } from './routes';
import { CORE_PAGE_KEYS, PAGE_ROUTES, type PageKey } from './pageRegistry';
import { recordPath } from './recordPages';
import { PublicQuestionnairePage } from '../components/questionnaire';
import Login from '../components/Login';
import DevLoginScreen from '../components/DevLoginScreen';
import { DEV_LOGIN_ENABLED, DEV_LOGIN_PATH } from '../auth/devAuth';
import type { AuthContext } from '../components/Login';
import SessionExpiredDialog from '../components/SessionExpiredDialog';
import { MarkLoader } from '../components/brand/MarkLoader';
import { StartingScreen } from '../components/brand/StartingScreen';
import {
  buildNavRows,
  groupLandingFor,
  isRowDivider,
  planeOfSection,
  type NavRow,
  type NavRowItem,
  type Plane,
} from '../components/navItems';
import { RecordTab, RecordTabLanding } from '../components/record/RecordTabLanding';
import { useSelectedTenant } from '../contexts/useSelectedTenant';

const LifestyleDashboard          = lazy(() => import('../components/LifestyleDashboard'));
const TenantList                  = lazy(() => import('../components/admin/TenantList'));
const TenantPage                  = lazy(() => import('../components/admin/tenant/TenantPage'));
const TenantGeneral               = lazy(() => import('../components/admin/tenant/TenantGeneral'));
const TenantAdministrators        = lazy(() => import('../components/admin/tenant/TenantAdministrators'));
const OrganisationList            = lazy(() => import('../components/admin/OrganisationList'));
const OrganisationPage            = lazy(() => import('../components/admin/organisation/OrganisationPage'));
const OrganisationGeneral         = lazy(() => import('../components/admin/organisation/OrganisationGeneral'));
const OrganisationPeople          = lazy(() => import('../components/admin/organisation/OrganisationPeople'));
const ManageQuestionnaires        = lazy(() => import('../components/ManageQuestionnaires/ManageQuestionnaires'));
const QuestionnairePage           = lazy(() => import('../components/ManageQuestionnaires/QuestionnairePage'));
const QuestionnaireQuestions      = lazy(() => import('../components/ManageQuestionnaires/QuestionnaireQuestions'));
const QuestionnaireSubmissions    = lazy(() => import('../components/ManageQuestionnaires/QuestionnaireSubmissions'));
const Dashboard                   = lazy(() => import('../components/DashboardAndGraphs/Dashboard'));
const MetabolicMeasurementsPage   = lazy(() => import('../components/MetabolicMeasurementsPage/MetabolicMeasurementPg'));
const ProgrammesOverview          = lazy(() => import('../components/ProgrammesOverview/ProgrammesOverview'));
const SavedDocumentsPage          = lazy(() => import('../components/documents/SavedDocumentsPage'));
const DocumentPage                = lazy(() => import('../components/documents/DocumentPage'));
const DocumentTab             = lazy(() => import('../components/documents/DocumentTab'));
const ManageDocumentTypes         = lazy(() => import('../components/admin/DocumentTypes/ManageDocumentTypes'));
const DocumentExtractionReviewPage = lazy(() => import('../components/documents/DocumentExtractionReviewPage'));
const SharedUsersList             = lazy(() => import('../components/community').then(m => ({ default: m.SharedUsersList })));
// One chunk boundary per screen, all from the coaching barrel: the five share
// most of their component tree, so splitting them apart would ship it five times.
const CoachesLayout               = lazy(() => import('../components/coaching').then(m => ({ default: m.CoachesLayout })));
const MyCoachesPage               = lazy(() => import('../components/coaching').then(m => ({ default: m.MyCoachesPage })));
const CoachHistoryPage            = lazy(() => import('../components/coaching').then(m => ({ default: m.CoachHistoryPage })));
const MyCoachListingPage          = lazy(() => import('../components/coaching').then(m => ({ default: m.MyCoachListingPage })));
const CoachRequestsPage           = lazy(() => import('../components/coaching').then(m => ({ default: m.CoachRequestsPage })));
const CoachMembersPage            = lazy(() => import('../components/coaching').then(m => ({ default: m.CoachMembersPage })));
const CoachMemberDetailPage       = lazy(() => import('../components/coaching').then(m => ({ default: m.CoachMemberDetailPage })));
const ProfilePage                 = lazy(() => import('../components/ProfilePage/ProfilePage'));
const DataPermissionsPage         = lazy(() => import('../components/DataPermissionsPage/DataPermissionsPage'));
const DataSourcePage              = lazy(() => import('../components/DataProvidersPage/DataSourcePage'));
const DataSourceGeneral           = lazy(() => import('../components/DataProvidersPage/DataSourceGeneral'));
const Agent                       = lazy(() => import('../components/Agents'));
const UserSettings                = lazy(() => import('../components/UserSettings'));
const TenantSettings              = lazy(() => import('../components/TenantSettings'));
const MeasurementList             = lazy(() => import('../components/MasterDataManagement/MeasurementList'));
const MeasurementPage             = lazy(() => import('../components/MasterDataManagement/MeasurementPage'));
const MeasurementGeneral          = lazy(() => import('../components/MasterDataManagement/MeasurementGeneral'));
const MeasurementUnits            = lazy(() => import('../components/MasterDataManagement/MeasurementUnits'));
const UnitList                    = lazy(() => import('../components/MasterDataManagement/UnitList'));
const MolarMassList               = lazy(() => import('../components/MasterDataManagement/MolarMassList'));
const ManageProgrammes            = lazy(() => import('../components/ManageProgrammes/ManageProgrammes'));
const ProgrammePage               = lazy(() => import('../components/ManageProgrammes/ProgrammePage'));
const ProgrammeGeneral            = lazy(() => import('../components/ManageProgrammes/ProgrammeGeneral'));
const ProgrammeMeasurementsTab    = lazy(() => import('../components/ManageProgrammes/ProgrammePage').then(m => ({ default: m.ProgrammeMeasurementsTab })));
const ProgrammeDashboardsTab      = lazy(() => import('../components/ManageProgrammes/ProgrammePage').then(m => ({ default: m.ProgrammeDashboardsTab })));
const ProgrammePeople             = lazy(() => import('../components/ManageProgrammes/ProgrammePeople'));
const ManageProgrammeMeasurements = lazy(() => import('../components/ManageProgrammeMeasurements/ManageProgrammeMeasurements'));
const ManageUsers                 = lazy(() => import('../components/ManageUsers'));
const ManageRoles                 = lazy(() => import('../components/ManageRoles'));
const TenantRolePermissions       = lazy(() => import('../components/roles/TenantRolePermissions'));

const Spinner = () => (
  <Box styles={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <MarkLoader size="page" />
  </Box>
);

/** Shorthand Suspense wrapper to keep the route tree readable. */
const S = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<Spinner />}>{children}</Suspense>
);

// ─── Page-access guard ────────────────────────────────────────────────────────

/**
 * The first row of one plane of the reader's menu, at this build's own address
 * for its page. The layout's path is the database's, and a path this build does
 * not route would fall to a catch-all and render nothing, so the route table
 * decides, as it does for a group's own address.
 */
const firstRouteInPlane = (rows: readonly NavRowItem[], plane: Plane): string | null => {
  const row = rows.find(
    (item): item is NavRow => !isRowDivider(item) && planeOfSection(item.section) === plane,
  );
  if (row === undefined) return null;
  const page = row.kind === 'page' ? row.page : row.tabs[0]?.page;
  return page === undefined ? null : PAGE_ROUTES[page];
};

/**
 * Somewhere this user can actually go: the first row of their menu.
 *
 * The first page they *do* hold rather than a fixed one: with a trimmed
 * deployment even the dashboard may be absent, and bouncing someone to another
 * page they cannot see would loop. The organisation's menu before the
 * platform's, so a platform administrator in no organisation opens on the
 * platform's first row. Without a layout, the registry's order stands in, and
 * `ROUTES.profile` is the last resort because it is a core page and therefore
 * always granted.
 */
const fallbackPathFor = (
  allowedPages: ReadonlySet<PageKey>,
  navigation?: AuthContext['navigation'],
): string => {
  const rows = buildNavRows(allowedPages, navigation?.groups ?? [], navigation?.layout ?? []);
  const first = firstRouteInPlane(rows, 'organisation') ?? firstRouteInPlane(rows, 'global');
  if (first !== null) return first;
  const held = (Object.keys(PAGE_ROUTES) as PageKey[]).find((key) =>
    allowedPages.has(key),
  );
  return held ? PAGE_ROUTES[held] : ROUTES.profile;
};

/** The platform's own address opens on the first of its rows the reader holds. */
const PlatformLanding = () => {
  const authContext = useOutletContext<AuthContext | null>();
  const allowedPages = authContext?.allowedPages ?? new Set<PageKey>(CORE_PAGE_KEYS);
  const navigation = authContext?.navigation;
  const rows = buildNavRows(allowedPages, navigation?.groups ?? [], navigation?.layout ?? []);
  return (
    <Navigate to={firstRouteInPlane(rows, 'global') ?? fallbackPathFor(allowedPages, navigation)} replace />
  );
};

/**
 * Blocks a route whose page has not been granted to any of the user's roles.
 *
 * Hiding the navigation entry is not enough on its own — the URL is still
 * typeable and links survive in bookmarks and mails — so every governed route
 * is wrapped here.
 */
const RequirePage = ({ page, children }: { page: PageKey; children: React.ReactNode }) => {
  const authContext = useOutletContext<AuthContext | null>();

  // A shell rendered outside the authenticated tree supplies no context. Fall
  // back to the core pages rather than to "deny everything", so a missing
  // context degrades to a reduced app instead of an endless redirect.
  const allowedPages = authContext?.allowedPages ?? new Set<PageKey>(CORE_PAGE_KEYS);
  if (allowedPages.has(page)) return <>{children}</>;

  return <Navigate to={fallbackPathFor(allowedPages, authContext?.navigation)} replace />;
};

/**
 * Resolves a group's own address to one of its screens.
 *
 * '/coaches' and '/admin/master-data' name a sidebar row rather than a page, so
 * what they open depends on the reader's grants: the first tab they hold, from
 * the same layout the sidebar row reads. A bookmark from before V60 still
 * works, and a reader without the first tab lands on one they do have.
 *
 * `defaultPage` covers a layout naming nothing inside `rowPath` — a failed
 * `/pages/me`, or a database still on V59, which answers that `coaches` is at
 * this very address (the two are separate deploy artefacts). Following that
 * would not loop: `Navigate` fires from an effect keyed on the resolved path,
 * so it redirects once and then renders null, leaving a page header above
 * nothing. Harder to find than a loop, which is why the route table decides.
 *
 * `group` is a nav_group key and `defaultPage` a page key; the coaching pair
 * spelling the same is a coincidence.
 */
const GroupLanding = ({
  group,
  rowPath,
  defaultPage,
}: {
  group: string;
  rowPath: string;
  defaultPage: PageKey;
}) => {
  const authContext = useOutletContext<AuthContext | null>();
  const allowedPages = authContext?.allowedPages ?? new Set<PageKey>(CORE_PAGE_KEYS);

  // `navigation?` as well as `authContext?`: the field is non-optional in the
  // type, but the shells already treat it as absentable for the prop-driven
  // case, and a throw here would take the whole route down.
  const fromLayout = groupLandingFor(
    group,
    allowedPages,
    authContext?.navigation?.layout ?? [],
  );
  const inside = fromLayout !== null && fromLayout.startsWith(`${rowPath}/`);

  const landing =
    (inside ? fromLayout : null) ??
    (allowedPages.has(defaultPage) ? PAGE_ROUTES[defaultPage] : null);

  return <Navigate to={landing ?? fallbackPathFor(allowedPages, authContext?.navigation)} replace />;
};

/** Shorthand so the route tree stays readable: Suspense + page guard. */
const G = ({ page, children }: { page: PageKey; children: React.ReactNode }) => (
  <RequirePage page={page}>
    <Suspense fallback={<Spinner />}>{children}</Suspense>
  </RequirePage>
);

/**
 * Keeps the global administration area to global admins.
 *
 * The per-page guards inside it already refuse every child route — no other
 * role is ever granted an admin page — but without this a member typing /admin
 * would reach the area and be redirected page by page. Refusing at the top says
 * what is true: this area is not theirs.
 *
 * A layout route rather than a wrapper, since /admin stopped being its own
 * shell: it sits inside the one shell and forwards the outlet context onward.
 * That forwarding is load-bearing — a bare `<Outlet />` provides `undefined`,
 * so every page below would lose the context the shell handed down.
 *
 * A missing context means this rendered outside the authenticated tree, which
 * is left alone rather than redirected.
 */
const RequireGlobalAdmin = () => {
  const authContext = useOutletContext<AuthContext | null>();
  if (authContext && !authContext.isGlobalAdmin) return <Navigate to={ROUTES.root} replace />;
  return <Outlet context={authContext} />;
};

// ─── Lifestyle route wrappers ─────────────────────────────────────────────────

/**
 * The organisation the navbar names, not the first one the account holds.
 *
 * `tenantRoles[0]` was harmless while nobody was in two organisations. With a
 * switcher it is a mismatch you cannot see: the shell names Amsterdam and this
 * screen saves to Je Leefstijl Als Medicijn. The role still comes from
 * `tenantRoles`, because that is what carries it — `AdministeredTenant` is a
 * name and an id.
 */
const TenantSettingsRoute = () => {
  const { tenantRoles } = useOutletContext<AuthContext>();
  const { selected } = useSelectedTenant();
  const role =
    tenantRoles.find((held) => held.tenantId === selected?.tenantId) ?? null;
  if (!role) return <Navigate to={ROUTES.root} replace />;
  return <TenantSettings tenantId={role.tenantId} tenantName={role.tenantName} role={role.role} />;
};

const AgentRoute = () => {
  const { firstName } = useOutletContext<AuthContext>();
  return <Agent firstName={firstName} />;
};

const DashboardRoute = () => {
  const { firstName } = useOutletContext<AuthContext>();
  return <Dashboard firstName={firstName} />;
};

// ─── Admin route wrappers ─────────────────────────────────────────────────────

const AdminUsersPage = lazy(() => import('../components/admin/UserList'));
const DataProvidersPage = lazy(() => import('../components/DataProvidersPage/DataProvidersPage'));
const ManageDataProviders = lazy(() => import('../components/ManageDataProviders/ManageDataProviders'));
const DataProviderPage = lazy(() => import('../components/ManageDataProviders/DataProviderPage'));
const DataProviderGeneral = lazy(() => import('../components/ManageDataProviders/DataProviderGeneral'));
const DataProviderOffers = lazy(() => import('../components/ManageDataProviders/DataProviderOffers'));
const GlobalRoleList = lazy(() => import('../components/roles/GlobalRoleList'));
const GlobalPermissions = lazy(() => import('../components/roles/GlobalPermissions'));
const SystemRoleList = lazy(() => import('../components/roles/SystemRoleList'));
const SystemRolePermissions = lazy(() => import('../components/roles/SystemRolePermissions'));

/** A questionnaire's tab at the address it had under /admin, before V83. */
const MovedQuestionnaireTab = ({ segment }: { segment: 'questions' | 'submissions' }) => {
  const { id = '' } = useParams<{ id: string }>();
  return <Navigate to={recordPath('questionnaire', { questionnaireId: id }, segment)} replace />;
};

// ─── Route tree ───────────────────────────────────────────────────────────────

const AppRouter = () => (
  <UserPreferencesProvider>
    <LocalizationProvider>
      <PickerLocaleProvider>
      <LookProvider>
        <ToastProvider>
        <NotificationsProvider>
          <CssBaseline />
          {/* Outside the boundary below, so an expired session is still
              announced on a page that has crashed. */}
          <SessionExpiredDialog />
          {/* The themed half of the error boundary pair. Inside the theme,
              so an error on a page renders in the platform's own styling; the
              outer copy in index.tsx is outside every provider and is what
              catches a provider failing at startup, unstyled but always able to
              render. Nested boundaries mean the innermost one wins, so this is
              the copy a member actually sees. */}
          <AppErrorBoundary boundary="page">
  <BrowserRouter>
    <Routes>
      <Route path="/questionnaire/:token" element={<PublicQuestionnairePage />} />
      {DEV_LOGIN_ENABLED && <Route path={DEV_LOGIN_PATH} element={<DevLoginScreen />} />}
      <Route element={<Login />}>

        {/* ── The app ── */}
        {/* One shell for all three audiences. The member's app, the
            organisation's and the platform's differ by which sidebar rows they
            carry, which the server already answers per person (nav_section) —
            not by which chrome wraps them. There was a second shell for /admin
            until ADR 0027 made the administration area an ordinary part of one
            person's app rather than a different kind of place. */}
        {/* The shell is the last thing the start waits for, so it waits under the mark. */}
        <Route path="/" element={<Suspense fallback={<StartingScreen />}><LifestyleDashboard /></Suspense>}>
          <Route index element={<G page="dashboard"><DashboardRoute /></G>} />
          <Route path="measurements" element={<G page="measurements"><MetabolicMeasurementsPage /></G>} />
          <Route path="programmes" element={<G page="programmes"><ProgrammesOverview /></G>} />
          <Route path="documents" element={<G page="documents"><SavedDocumentsPage /></G>} />
          <Route path="documents/:documentId" element={<G page="documents"><DocumentPage /></G>}>
            <Route index element={<RecordTabLanding record="document" />} />
            <Route path="document" element={<RecordTab record="document" segment="document"><S><DocumentTab /></S></RecordTab>} />
            <Route path="*" element={<RecordTabLanding record="document" />} />
          </Route>
          <Route path="documents/:id/review" element={<G page="documents"><DocumentExtractionReviewPage /></G>} />
          <Route path="community" element={<G page="community"><SharedUsersList /></G>} />
          {/* One load, five guarded screens. The layout holds no page key of
              its own -- it is the group's row, and a group grants nothing --
              so a reader holding one of the five reaches that one alone. Its
              own address resolves to whichever of the five that is (V60);
              before, the first screen lived here and had no address at all. */}
          <Route path="coaches" element={<S><CoachesLayout /></S>}>
            <Route index element={<GroupLanding group="coaches" rowPath={ROUTES.coachesRow} defaultPage="coaches" />} />
            <Route path="mine" element={<G page="coaches"><MyCoachesPage /></G>} />
            <Route path="history" element={<G page="coaches-history"><CoachHistoryPage /></G>} />
            <Route path="listing" element={<G page="coach-listing"><MyCoachListingPage /></G>} />
            <Route path="requests" element={<G page="coach-requests"><CoachRequestsPage /></G>} />
            <Route path="members" element={<G page="coach-members"><CoachMembersPage /></G>} />
            {/* A flat sibling, not a nested child: the two share no chrome —
                the overview has a search box, the detail a back arrow — so an
                index/child pair would add an indirection rendering nothing.
                Same page key, so a reader who loses the grant loses both. */}
            <Route path="members/:userId" element={<G page="coach-members"><CoachMemberDetailPage /></G>} />
          </Route>
          <Route path="profile" element={<G page="profile"><ProfilePage hideDeleteButton={false} /></G>} />
          <Route path="agents" element={<G page="agents"><AgentRoute /></G>} />
          <Route path="data-providers" element={<G page="data-providers"><DataProvidersPage /></G>} />
          <Route path="data-providers/:providerId" element={<G page="data-providers"><DataSourcePage /></G>}>
            <Route index element={<RecordTabLanding record="dataSource" />} />
            <Route path="general" element={<RecordTab record="dataSource" segment="general"><S><DataSourceGeneral /></S></RecordTab>} />
            <Route path="*" element={<RecordTabLanding record="dataSource" />} />
          </Route>
          <Route path="data-permissions" element={<G page="data-permissions"><DataPermissionsPage /></G>} />
          <Route path="settings" element={<G page="settings"><UserSettings /></G>} />
          {/* This organisation's own administration, under one prefix (V61):
              '/' is the member's app, '/tenant/*' theirs, '/admin/*' the
              platform's — the same line nav_section already draws. Nothing at
              '/tenant' itself: two sidebar rows live in there, so it would have
              to answer for both. */}
          <Route path="tenant/manage" element={<G page="tenant-settings"><TenantSettingsRoute /></G>} />
          <Route path="tenant/members" element={<G page="manage-users"><ManageUsers /></G>} />
          <Route path="tenant/roles" element={<G page="manage-roles"><ManageRoles /></G>} />
          <Route path="tenant/role-permissions" element={<G page="manage-role-permissions"><TenantRolePermissions /></G>} />
          <Route path="tenant/programmes" element={<G page="manage-programmes"><ManageProgrammes /></G>} />
          {/* A programme's own page (decision 0072), declared in recordPages.ts:
              under the list's page key, and each tab under the grant the table
              gives it. A tab not held, or not known, opens the first one the
              reader holds. */}
          <Route path="tenant/programmes/:programmeId" element={<G page="manage-programmes"><ProgrammePage /></G>}>
            <Route index element={<RecordTabLanding record="programme" />} />
            <Route path="general" element={<RecordTab record="programme" segment="general"><S><ProgrammeGeneral /></S></RecordTab>} />
            <Route path="measurements" element={<RecordTab record="programme" segment="measurements"><S><ProgrammeMeasurementsTab /></S></RecordTab>} />
            <Route path="dashboards" element={<RecordTab record="programme" segment="dashboards"><S><ProgrammeDashboardsTab /></S></RecordTab>} />
            <Route path="people" element={<RecordTab record="programme" segment="people"><S><ProgrammePeople /></S></RecordTab>} />
            <Route path="*" element={<RecordTabLanding record="programme" />} />
          </Route>
          <Route path="tenant/programme-measurements" element={<G page="manage-measurements"><ManageProgrammeMeasurements /></G>} />
          <Route path="tenant/questionnaires" element={<G page="admin-questionnaires"><ManageQuestionnaires /></G>} />
          <Route path="tenant/questionnaires/:questionnaireId" element={<G page="admin-questionnaires"><QuestionnairePage /></G>}>
            <Route index element={<RecordTabLanding record="questionnaire" />} />
            <Route path="questions" element={<RecordTab record="questionnaire" segment="questions"><S><QuestionnaireQuestions /></S></RecordTab>} />
            <Route path="submissions" element={<RecordTab record="questionnaire" segment="submissions"><S><QuestionnaireSubmissions /></S></RecordTab>} />
            <Route path="*" element={<RecordTabLanding record="questionnaire" />} />
          </Route>
          {/* Where those five used to live. Bookmarkable, and the database is a
              separate deploy artefact: a build landing before V61 reads a layout
              that still links here, and without these the sidebar row would hit
              the catch-all and read as a revoked page. Unguarded on purpose —
              each one lands on a guarded route. */}
          <Route path="tenant-settings" element={<Navigate to={ROUTES.tenantManage} replace />} />
          <Route path="manage/users" element={<Navigate to={ROUTES.tenantMembers} replace />} />
          <Route path="manage/roles" element={<Navigate to={ROUTES.tenantRoles} replace />} />
          <Route path="manage/programmes" element={<Navigate to={ROUTES.tenantProgrammes} replace />} />
          <Route path="manage/measurements" element={<Navigate to={ROUTES.tenantProgrammeMeasurements} replace />} />
          {/* Questionnaires were the administration area's until V83 moved them
              to the organisation. Outside the admin layout, whose guard would
              send everyone but a Global Admin home first. */}
          <Route path="admin/questionnaires" element={<Navigate to={ROUTES.tenantQuestionnaires} replace />} />
          <Route path="admin/questionnaires/edit/:id" element={<MovedQuestionnaireTab segment="questions" />} />
          <Route path="admin/questionnaires/:id/submissions" element={<MovedQuestionnaireTab segment="submissions" />} />
          {/* The platform's own administration, the third prefix. A layout
              route rather than a shell: it contributes the guard and nothing
              visual. Child routes carry their parent's page key — an editor is
              part of the page that links to it, not a page of its own. */}
          <Route path="admin" element={<RequireGlobalAdmin />}>
            <Route index element={<PlatformLanding />} />
            <Route path="global-roles" element={<G page="admin-global-roles"><GlobalRoleList /></G>} />
            <Route path="global-permissions" element={<G page="admin-global-permissions"><GlobalPermissions /></G>} />
            <Route path="roles" element={<G page="admin-roles"><SystemRoleList /></G>} />
            <Route path="role-permissions" element={<G page="admin-role-permissions"><SystemRolePermissions /></G>} />
            <Route path="tenants" element={<G page="admin-tenants"><TenantList /></G>} />
            <Route path="tenants/:tenantId" element={<G page="admin-tenants"><TenantPage /></G>}>
              <Route index element={<RecordTabLanding record="tenant" />} />
              <Route path="general" element={<RecordTab record="tenant" segment="general"><S><TenantGeneral /></S></RecordTab>} />
              <Route path="administrators" element={<RecordTab record="tenant" segment="administrators"><S><TenantAdministrators /></S></RecordTab>} />
              <Route path="*" element={<RecordTabLanding record="tenant" />} />
            </Route>
            <Route path="organisations" element={<G page="admin-organisations"><OrganisationList /></G>} />
            <Route path="organisations/:organisationId" element={<G page="admin-organisations"><OrganisationPage /></G>}>
              <Route index element={<RecordTabLanding record="organisation" />} />
              <Route path="general" element={<RecordTab record="organisation" segment="general"><S><OrganisationGeneral /></S></RecordTab>} />
              <Route path="people" element={<RecordTab record="organisation" segment="people"><S><OrganisationPeople /></S></RecordTab>} />
              <Route path="*" element={<RecordTabLanding record="organisation" />} />
            </Route>
            <Route path="users" element={<G page="admin-users"><AdminUsersPage /></G>} />
            <Route path="data-providers" element={<G page="admin-data-providers"><ManageDataProviders /></G>} />
            <Route path="document-types" element={<G page="admin-document-types"><ManageDocumentTypes /></G>} />
            <Route path="data-providers/:providerId" element={<G page="admin-data-providers"><DataProviderPage /></G>}>
              <Route index element={<RecordTabLanding record="dataProvider" />} />
              <Route path="general" element={<RecordTab record="dataProvider" segment="general"><S><DataProviderGeneral /></S></RecordTab>} />
              <Route path="offers" element={<RecordTab record="dataProvider" segment="offers"><S><DataProviderOffers /></S></RecordTab>} />
              <Route path="*" element={<RecordTabLanding record="dataProvider" />} />
            </Route>
            {/* Master data is the platform's catalogue, not an organisation's:
                measurement, unit and measurement_unit carry no tenant_id, and
                measurement_unit holds the conversion factors every tenant's
                stored values are read through (V59). Two grids, a
                measurement's page holding its units, and an address for the
                row that resolves to one of the two. Links and All data were
                withdrawn in V86; their addresses are bookmarks now. */}
            <Route path="master-data">
              <Route index element={<GroupLanding group="master-data" rowPath={ROUTES.masterDataRow} defaultPage="manage-data" />} />
              <Route path="measurements" element={<G page="manage-data"><MeasurementList /></G>} />
              <Route path="measurements/:measurementId" element={<G page="manage-data"><MeasurementPage /></G>}>
                <Route index element={<RecordTabLanding record="measurement" />} />
                <Route path="general" element={<RecordTab record="measurement" segment="general"><S><MeasurementGeneral /></S></RecordTab>} />
                <Route path="units" element={<RecordTab record="measurement" segment="units"><S><MeasurementUnits /></S></RecordTab>} />
                <Route path="*" element={<RecordTabLanding record="measurement" />} />
              </Route>
              <Route path="units" element={<G page="master-data-units"><UnitList /></G>} />
              <Route path="units/molar-masses" element={<G page="master-data-units"><MolarMassList /></G>} />
              <Route path="links" element={<Navigate to={ROUTES.masterDataMeasurements} replace />} />
              <Route path="all" element={<Navigate to={ROUTES.masterDataMeasurements} replace />} />
            </Route>
            <Route path="*" element={<PlatformLanding />} />
          </Route>
          <Route path="*" element={<Navigate to={ROUTES.root} replace />} />
        </Route>

      </Route>
    </Routes>
  </BrowserRouter>
          </AppErrorBoundary>
        </NotificationsProvider>
        </ToastProvider>
      </LookProvider>
      </PickerLocaleProvider>
    </LocalizationProvider>
  </UserPreferencesProvider>
);

export default AppRouter;
